import React from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { Column } from '../../../components/DataTable/DataTable';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { formatDate as formatDateGlobal, formatDateTime as formatDateTimeGlobal, APP_CONFIG } from '../../../config/appConfig';
import { StatusDotPill } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { cn } from '../../../lib/utils';
import { deleteSupplierQuotation } from '../../../services/mutations';
import { fetcher } from '../../../services/fetcher';
import type { SupplierQuotation } from './useData';
import { computePurchaseOrderTotals } from '../supply-purchase-orders/purchaseOrderTotals';
import { supplyListMoneySpanClass, SupplyListLineItemCountCell } from '../supplyListMoneyDisplay';
import { supplyBlockedActionMessage } from '../blockingMessages';

export const moduleConfig = {
  module: {
    title: 'Quotations',
    permissionModuleKey: 'supplier_quotations',
    route: {
      list: '/supplies/supplier-quotations',
      new: '/supplies/supplier-quotations/new',
      detail: (id: number) => `/supplies/supplier-quotations/${id}`,
      edit: (id: number) => `/supplies/supplier-quotations/${id}/edit`},
    api: {
      list: '/supplier-quotations',
      detail: (id: number) => `/supplier-quotations/${id}`}},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search by reference, notes, case, enquiry or supplier…' },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '',
      fields: ['vessel', 'caseRef', 'ref', 'quoDate', 'enquiryRef', 'supplier']},
  ],
  filters: {
    fields: [
      {
        key: 'vessel',
        label: 'Vessel',
        type: 'select' as const,
        placeholder: 'All vessels',
        getValue: (item: SupplierQuotation) => item.supplyCase?.vessel?.vesselName?.trim() || '',
        options: []},
      {
        key: 'caseRef',
        label: 'Case',
        type: 'select' as const,
        placeholder: 'All cases',
        getValue: (item: SupplierQuotation) => item.supplyCase?.ref || '',
        options: []},
      {
        key: 'ref',
        label: 'Reference',
        placeholder: 'All references',
        getValue: (item: SupplierQuotation) => item.ref || '',
        options: []},
      {
        key: 'quoDate',
        label: 'Quotation date',
        type: 'date' as const,
        placeholder: 'All dates',
        getValue: (item: SupplierQuotation) => item.quoDate || '',
        options: []},
      {
        key: 'enquiryRef',
        label: 'Enquiry',
        type: 'select' as const,
        placeholder: 'All enquiries',
        getValue: (item: SupplierQuotation) => item.enquiry?.ref || '',
        options: []},
      {
        key: 'supplier',
        label: 'Supplier',
        type: 'select' as const,
        placeholder: 'All suppliers',
        getValue: (item: SupplierQuotation) => item.supplier?.name || '',
        options: []},
    ],
    applyFilter: (
      items: SupplierQuotation[],
      filters: Record<string, string[] | string>,
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>,
    ) => {
      const asArray = (v: string[] | string | undefined): string[] =>
        Array.isArray(v) ? v : typeof v === 'string' && v !== '' ? [v] : [];
      const toDateOnly = (s?: string | null): string => (s ? String(s).slice(0, 10) : '');
      return items.filter((item) => {
        const vesselFilters = asArray(filters.vessel);
        if (vesselFilters.length) {
          const vesselName = (item.supplyCase?.vessel?.vesselName || '').toLowerCase();
          if (!vesselFilters.some((f) => vesselName.includes(String(f).toLowerCase()))) return false;
        }

        const caseFilters = asArray(filters.caseRef);
        if (caseFilters.length) {
          const caseRef = (item.supplyCase?.ref || '').toLowerCase();
          if (!caseFilters.some((f) => caseRef.includes(String(f).toLowerCase()))) return false;
        }

        const refFilters = asArray(filters.ref);
        if (refFilters.length) {
          const v = item.ref || '';
          if (!refFilters.some((f) => v.toLowerCase().includes(String(f).toLowerCase()))) return false;
        }

        const quoDateFilter = dateRanges?.quoDate;
        if (quoDateFilter?.start || quoDateFilter?.end) {
          const itemDateRaw = item.quoDate ? new Date(item.quoDate) : null;
          if (!itemDateRaw || Number.isNaN(itemDateRaw.getTime())) return false;
          if (quoDateFilter.start && itemDateRaw < quoDateFilter.start) return false;
          if (quoDateFilter.end && itemDateRaw > quoDateFilter.end) return false;
        } else {
          const quoDateValues = asArray(filters.quoDate);
          if (quoDateValues.length) {
            const itemDate = toDateOnly(item.quoDate);
            if (!quoDateValues.some((f) => itemDate.includes(String(f).slice(0, 10)))) return false;
          }
        }

        const enquiryFilters = asArray(filters.enquiryRef);
        if (enquiryFilters.length) {
          const enquiryRef = (item.enquiry?.ref || '').toLowerCase();
          if (!enquiryFilters.some((f) => enquiryRef.includes(String(f).toLowerCase()))) return false;
        }

        const supplierFilters = asArray(filters.supplier);
        if (supplierFilters.length) {
          const supplierName = (item.supplier?.name || '').toLowerCase();
          if (!supplierFilters.some((f) => supplierName.includes(String(f).toLowerCase()))) return false;
        }
        return true;
      });
    },
    applySearch: (items: SupplierQuotation[], searchTerm: string) => {
      const q = searchTerm.toLowerCase();
      return items.filter((item) => {
        const parts = [
          item.ref,
          item.notes,
          item.supplierNotes,
          item.supplyCase?.ref,
          item.enquiry?.ref,
          item.requisition?.ref,
          String(item.id),
          String(item.supplierId),
          item.supplier?.name,
          item.supplier?.code,
          item.currency?.code,
          item.currency?.name,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return parts.includes(q);
      });
    }},
  toolbar: {
    actions: []},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: SupplierQuotation) => item.id,
  },
  emptyStates: {
    noData: 'Create a quotation to get started.',
    noSearchResults: 'No quotations match your search.',
    noFilterResults: 'No quotations match the filters.',
  },
  delete: {
    title: 'Delete quotation',
    message: 'Delete this quotation and all its lines?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete quotation.',
    handler: async (id: number) => {
      await deleteSupplierQuotation(id);
    }},
  utils: {
    formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '')},
  form: {
    new: { title: 'New quotation', subtitle: 'Custom form', submitText: 'Create' },
    edit: { title: 'Edit quotation', submitText: 'Save' },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.'},
  fields: [] as FieldConfig[]};

const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value || ''}</span>;
};

/** Path must stay in sync with `moduleConfig.route.caseView` in `supply-cases/config.tsx`. */
function supplierQuotationCaseIdForView(row: SupplierQuotation | undefined): number | null {
  const id = row?.supplyCase?.id ?? row?.caseId ?? 0;
  return id > 0 ? id : null;
}

const QuotationCaseColumnCell: React.FC<{ row: SupplierQuotation }> = ({ row }) => {
  const { colors } = useTheme();
  const caseId = supplierQuotationCaseIdForView(row);
  if (caseId == null) {
    return (
      <div className="max-w-[16rem] py-0.5">
        <span className={cn('text-sm', colors.textSecondary)}>—</span>
      </div>
    );
  }
  const displayRef = row.supplyCase?.ref?.trim() || `#${caseId}`;
  const href = `/supplies/supply-cases/${caseId}/case-view`;
  const iconBtnClass =
    'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-indigo-600 transition-colors hover:bg-indigo-50 hover:text-indigo-900 dark:text-indigo-400 dark:hover:bg-indigo-950/50 dark:hover:text-indigo-300';

  return (
    <div className="flex max-w-[16rem] items-center gap-0.5 py-0.5">
      <Link
        to={href}
        className={iconBtnClass}
        title="Open case view"
        aria-label="Open case view"
        onClick={(e) => e.stopPropagation()}
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
          />
        </svg>
      </Link>
      <span className={cn('min-w-0 flex-1 truncate text-sm', colors.text)} title={displayRef}>
        {displayRef}
      </span>
    </div>
  );
};

function quotationStatusDotClass(submittedAt: string | null | undefined): string {
  return submittedAt ? 'bg-emerald-500 dark:bg-emerald-400' : 'bg-amber-500 dark:bg-amber-400';
}

const QuotationStatusCell: React.FC<{ row: SupplierQuotation }> = ({ row }) => {
  const submittedAt = row.enquiry?.rfqPublicSubmittedAt;
  const isSubmitted = Boolean(submittedAt);
  const label = isSubmitted ? 'Submitted' : 'Draft';
  const dotClass = quotationStatusDotClass(submittedAt);
  return (
    <div className="max-w-[10rem] py-0.5">
      <StatusDotPill
        dotClassName={dotClass}
        title={isSubmitted ? 'Quotation submitted' : 'Quotation draft'}
      >
        {label}
      </StatusDotPill>
    </div>
  );
};

const fmtMoney = (n: number, suffix?: string) =>
  `${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)}${suffix ? ` ${suffix}` : ''}`;

const QuotationMoneyCell: React.FC<{
  row: SupplierQuotation | undefined;
  variant: 'expenses' | 'items' | 'grand' | 'usd';
}> = ({ row, variant }) => {
  const { colors } = useTheme();
  if (!row) {
    return (
      <span className={cn('block w-full min-w-[6.75rem] text-right text-sm', colors.textSecondary)}>—</span>
    );
  }
  const t = computePurchaseOrderTotals(row);
  const ccy = row.currency?.code ?? '';
  if (variant === 'expenses') {
    return (
      <span
        className={supplyListMoneySpanClass('expenses', colors)}
        title={
          ccy
            ? `Sum of expense value + VAT in quotation currency (${ccy})`
            : 'Sum of expense value + VAT in quotation currency'
        }
      >
        {fmtMoney(t.expensesSum)}
      </span>
    );
  }
  if (variant === 'items') {
    return (
      <span
        className={supplyListMoneySpanClass('items', colors)}
        title={
          ccy ? `Sum of line totals in quotation currency (${ccy})` : 'Sum of line totals in quotation currency'
        }
      >
        {fmtMoney(t.itemsSum)}
      </span>
    );
  }
  if (variant === 'grand') {
    return (
      <span
        className={supplyListMoneySpanClass('grand', colors)}
        title={
          ccy
            ? `Items total + expenses in quotation currency (${ccy})`
            : 'Items total + expenses in quotation currency'
        }
      >
        {fmtMoney(t.grandTotal)}
      </span>
    );
  }
  return (
    <span
      className={supplyListMoneySpanClass('usd', colors, t.grandTotalUsd != null)}
      title="(Line totals + expenses) × rate, in USD"
    >
      {t.grandTotalUsd != null ? fmtMoney(t.grandTotalUsd) : '—'}
    </span>
  );
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<SupplierQuotation>[] => [
  {
    key: 'vessel',
    header: 'Vessel',
    sortable: true,
    render: (_v, row) => <TextCell value={row?.supplyCase?.vessel?.vesselName} />},
  { key: 'ref', header: 'Reference', sortable: true, render: (v, row) => <TextCell value={v ?? row?.ref} /> },
  { key: 'quoDate', header: 'Quotation date', sortable: true, render: (v) => formatDate(v) },
  {
    key: 'supplyCase.ref',
    header: 'Case',
    sortable: true,
    maxWidth: '16rem',
    render: (_v, row) => (row ? <QuotationCaseColumnCell row={row} /> : <TextCell value="" />)},
  {
    key: 'enquiry.ref',
    header: 'Enquiry',
    sortable: true,
    render: (_v, row) => <TextCell value={row?.enquiry?.ref ?? (row?.enqId ? `#${row.enqId}` : '')} />},
  {
    key: 'supplier.name',
    header: 'Supplier',
    sortable: true,
    render: (_v, row) => (
      <TextCell
        value={
          row?.supplier?.name
            ? row.supplier.name
            : row?.supplierId != null
              ? String(row.supplierId)
              : ''
        }
      />
    )},
  {
    key: 'lineItems',
    header: 'Items',
    sortable: true,
    align: 'right',
    render: (_v, row) => (
      <SupplyListLineItemCountCell count={row?._count?.lineItems ?? row?.lineItems?.length ?? 0} />
    )},
  {
    key: 'currency.code',
    header: 'CCY',
    sortable: true,
    render: (_v, row) => <TextCell value={row?.currency?.code} />},
  {
    key: 'expensesSum',
    header: 'Expenses',
    sortable: false,
    align: 'right',
    render: (_v, row) => <QuotationMoneyCell row={row} variant="expenses" />},
  {
    key: 'itemsSum',
    header: 'Items Total',
    sortable: false,
    align: 'right',
    render: (_v, row) => <QuotationMoneyCell row={row} variant="items" />},
  {
    key: 'grandTotal',
    header: 'Grand Total',
    sortable: false,
    align: 'right',
    render: (_v, row) => <QuotationMoneyCell row={row} variant="grand" />},
  {
    key: 'grandTotalUsd',
    header: 'Grand Total ($)',
    sortable: false,
    align: 'right',
    render: (_v, row) => <QuotationMoneyCell row={row} variant="usd" />},
  { key: 'createdAt', header: 'Created', sortable: true, render: (v) => formatDate(v) },
  {
    key: 'quotationStatus',
    header: 'Quotation status',
    sortable: false,
    render: (_v, row) => (row ? <QuotationStatusCell row={row} /> : <TextCell value="" />)},
  {
    key: 'enquiry.rfqPublicSubmittedAt',
    header: 'Date submitted',
    sortable: true,
    render: (_v, row) => <TextCell value={row?.enquiry?.rfqPublicSubmittedAt ? formatDateTimeGlobal(row.enquiry.rfqPublicSubmittedAt) : ''} />},
];

const TableActionsCell: React.FC<{
  item: SupplierQuotation;
  onDelete: (id: number) => void;
  canDelete?: boolean;
  canEdit?: boolean;
}> = ({ item, onDelete, canDelete = true, canEdit = true }) => {
  const { showError } = useNotification();
  const { data: evaluationsRes } = useSWR<any>(`/quotation-evaluations?page=1&limit=1000`, fetcher);
  const { data: purchaseOrdersRes } = useSWR<any>(`/supply-purchase-orders?page=1&limit=1000`, fetcher);

  const hasCaseAssigned = (item.caseId ?? 0) > 0;
  const linkedOrder = React.useMemo(() => {
    const rows = Array.isArray(purchaseOrdersRes) ? purchaseOrdersRes : purchaseOrdersRes?.data ?? [];
    return rows.some((po: any) => Number(po?.quotationId) === item.id);
  }, [purchaseOrdersRes, item.id]);
  const linkedEvaluation = React.useMemo(() => {
    const rows = Array.isArray(evaluationsRes) ? evaluationsRes : evaluationsRes?.data ?? [];
    return rows.some((ev: any) => {
      const ids = [
        ev?.quotation1Id,
        ev?.quotation2Id,
        ev?.quotation3Id,
        ev?.quotation4Id,
        ev?.quotation5Id,
      ]
        .map((v) => (v == null ? null : Number(v)))
        .filter((v): v is number => Number.isFinite(v));
      const summaryByQuote = Array.isArray(ev?.summary?.byQuote) ? ev.summary.byQuote : [];
      const summaryIds = summaryByQuote
        .map((b: any) => Number(b?.quotationId))
        .filter((v: number) => Number.isFinite(v));
      return [...ids, ...summaryIds].includes(item.id);
    });
  }, [evaluationsRes, item.id]);
  const linkedByEvaluationOrOrder = linkedEvaluation || linkedOrder;

  const deleteBlockedByCase = hasCaseAssigned;
  const deleteBlockedByEvaluationOrOrder = linkedByEvaluationOrOrder;
  const editBlockedByEvaluationOrOrder = linkedByEvaluationOrOrder;

  const deleteEnabled = canDelete && !deleteBlockedByCase && !deleteBlockedByEvaluationOrOrder;
  const editEnabled = canEdit && !editBlockedByEvaluationOrOrder;

  const onDeleteBlocked = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (deleteBlockedByEvaluationOrOrder) {
      showError(`Cannot delete: ${supplyBlockedActionMessage('QUO_DELETE_LINKED_EVAL_OR_ORDER')}`);
      return;
    }
    if (deleteBlockedByCase) {
      showError(`Cannot delete: ${supplyBlockedActionMessage('QUO_DELETE_CASE_ASSIGNED')}`);
    }
  };
  const onEditBlocked = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (editBlockedByEvaluationOrOrder) {
      showError(`Cannot edit: ${supplyBlockedActionMessage('QUO_EDIT_LINKED_EVAL_OR_ORDER')}`);
    }
  };

  return (
    <>
      <div className="flex items-center flex-nowrap">
        <Link
          to={moduleConfig.module.route.detail(item.id)}
          className="p-1 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-lg transition-colors"
          onClick={(e) => e.stopPropagation()}
          title="View"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        </Link>
        {canEdit && editEnabled ? (
          <Link
            to={moduleConfig.module.route.edit(item.id)}
            className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
            onClick={(e) => e.stopPropagation()}
            title="Edit"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </Link>
        ) : canEdit ? (
          <button
            type="button"
            onClick={onEditBlocked}
            className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
            title="Edit"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
        ) : null}
        {canDelete && deleteEnabled ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(item.id);
            }}
            className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        ) : canDelete ? (
          <button
            type="button"
            onClick={onDeleteBlocked}
            className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        ) : null}
      </div>
    </>
  );
};

export const getTableActions = (
  item: SupplierQuotation,
  onDelete: (id: number) => void,
  _c?: unknown,
  _ctx?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean },
): React.ReactNode => (
  <TableActionsCell item={item} onDelete={onDelete} canDelete={options?.canDelete !== false} canEdit={options?.canEdit !== false} />
);

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  if (iconName === 'add') {
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    );
  }
  return null;
};

